import type {
  ItemDeFatura,
  PagadorDaFatura,
  ResumoDeFatura,
  SimulacaoDeFechamento,
} from "@/api/contrato";
import { FalhaDaApi } from "@/api/problema";
import type { Matiz } from "@/componentes/PilulaDeSituacao";
import { competencia } from "@/formato";

/*
 * A fatura no painel: rótulos, cores e a leitura das recusas próprias do
 * faturamento (change `add-faturamento`).
 */

/**
 * A pílula da fatura.
 *
 * `vencida` tem precedência sobre `fechada`: é a mesma fatura, sem baixa e com
 * o vencimento para trás — e é a única que pede ação de alguém. A situação
 * desconhecida aparece crua, em neutro, pelo mesmo motivo da pílula de boleto:
 * o conjunto pode crescer, e a tela não esconde o que não conhece.
 */
export function aparenciaDaFatura(fatura: Pick<ResumoDeFatura, "situacao" | "vencida">): {
  matiz: Matiz;
  rotulo: string;
} {
  if (fatura.situacao === "fechada" && fatura.vencida) return { matiz: "falha", rotulo: "vencida" };
  switch (fatura.situacao) {
    case "fechada":
      return { matiz: "confirmado", rotulo: "fechada" };
    case "paga":
      return { matiz: "pago", rotulo: "paga" };
    case "cancelada":
      return { matiz: "encerrado", rotulo: "cancelada" };
    default:
      return { matiz: "neutro", rotulo: String(fatura.situacao) };
  }
}

/**
 * Quem paga: o cedente, em `por_cedente`; a aplicação, em `por_sistema`. É o
 * nome que encabeça a linha — a aplicação do cedente vai embaixo, como dado.
 */
export function nomeDoPagador(pagador: PagadorDaFatura): string {
  return pagador.pessoa_nome ?? pagador.sistema_nome;
}

/** O rótulo do item, com a competência de origem quando ela não é a da fatura. */
export function rotuloDoItem(item: ItemDeFatura): string {
  switch (item.tipo) {
    case "mensalidade":
      return "Mensalidade";
    case "excedente":
      return "Excedentes";
    case "ajuste":
      return `Ajuste de ${competencia(item.competencia_origem)}`;
    default:
      return String(item.tipo);
  }
}

/** A soma dos itens de um tipo, em centavos. */
export function somaDos(itens: readonly ItemDeFatura[], tipo: ItemDeFatura["tipo"]): number {
  return itens.filter((item) => item.tipo === tipo).reduce((soma, item) => soma + item.valor_centavos, 0);
}

export const SITUACOES_DE_FATURA: readonly (readonly [string, string])[] = [
  ["", "Todas as situações"],
  ["fechada", "Fechada"],
  ["paga", "Paga"],
  ["cancelada", "Cancelada"],
];

/**
 * O `409 simulacao_desatualizada`, quando é ele: a simulação ATUAL, cuja
 * `marca` o pedido seguinte repete.
 */
export function simulacaoDesatualizada(falha: unknown): SimulacaoDeFechamento | null {
  if (!(falha instanceof FalhaDaApi) || falha.codigo !== "simulacao_desatualizada") return null;
  const simulacao = (falha.problema as { simulacao?: SimulacaoDeFechamento } | null)?.simulacao;
  return typeof simulacao?.marca === "string" ? simulacao : null;
}

/** O campo que a recusa aponta, quando a API apontou um. */
export function campoDaRecusa(falha: unknown): string | null {
  return falha instanceof FalhaDaApi ? (falha.problema?.campo ?? null) : null;
}
